// src/utils/resolutionDraft.js
// ✅ 지출결의/수입결의 공용 유틸 — ExpenseResolutionNew.jsx/IncomeResolutionNew.jsx가
// 공유한다(중복 복사 대신 여기서 한 번만 정의).

// ✅ "결의서" 문서종류 판별 — 결재선 자동 생성의 합의 제외, 목록/상세 화면의
// draft_no "-R{n}" 접미 은닉 등 여러 곳에서 공통으로 쓰인다.
export const RESOLUTION_DOCUMENT_TYPES = ["expense_resolution", "income_resolution"];

// ✅ 사업구분 표시명 — DocumentTypeSelector.jsx의 CATEGORY_OPTIONS 라벨과 동일(전체
// 문서종류 합집합). 실제로 어떤 카테고리를 필터 목록에 보여줄지는 호출부가
// document_type별 실제 카테고리 목록(예: 수입기안엔 카페 카테고리 자체가 없음)으로
// 따로 제한한다.
export const CATEGORY_LABEL = {
  "경영팀": "경영팀",
  "용역사업": "용역",
  "환경/소독": "소독",
  "환경/종합": "종합",
  "CAFE_INN_가산": "가산",
  "CAFE_INN_독산": "독산",
  "CAFE_INN_금나래": "금나래",
  "CAFE_INN_그린": "그린",
  "테스트": "테스트",
};

export const PAY_METHOD_LABEL = {
  transfer: "계좌이체",
  card: "카드결제",
  cash: "현금결제",
};

export const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// ✅ 기안 draft_no(예: "카페가산지출-260714-001호")에서 "일자-순번" 부분만 추출 —
// 문서제목("[사업구분] 기안 제목-260714-001")에 쓰기 위함
export const extractDateSeq = (draftNo) => {
  if (!draftNo) return "";
  const m = String(draftNo).match(/(\d{6}-\d{3})호?\s*$/);
  return m ? m[1] : "";
};

// ✅ 예산/수입과목 원본(콤마+개행 구분 계층 문자열)을 ">" 구분자 표기로 변환
export const formatBudgetItem = (raw) => {
  if (!raw) return "";
  return raw
    .split(",")
    .map((s) => s.replace(/\n/g, " ").trim())
    .filter(Boolean)
    .join(">");
};

// ✅ 같은 기안으로 이미 결의서가 작성돼 있는지 확인(차단 아님, 확인만) — source_id가
// DjangoFilterBackend 필터 대상이 아니라서 목록을 받아 클라이언트에서 대조한다.
// resolutionDocumentType: "expense_resolution" | "income_resolution"
export async function countExistingResolutions(api, resolutionDocumentType, sourceDraftId) {
  try {
    const { data } = await api.get("/draft/documents/", {
      params: { document_type: resolutionDocumentType },
    });
    const list = Array.isArray(data) ? data : data.results || [];
    return list.filter((d) => d.source_app === "draft_document" && d.source_id === sourceDraftId).length;
  } catch (err) {
    console.error("❌ 기존 결의서 중복 확인 실패:", err);
    return 0;
  }
}

// ✅ 결의서 자체는 채번하지 않고 원본 기안 draft_no를 그대로 쓰되, DraftDocument.draft_no의
// unique 제약을 피하려고 모델 필드에만 "-R{순번}" 접미를 붙인다. 화면에는 절대 노출하지
// 않고(내부기안번호는 원본 draft_no 그대로 표시), 이 값은 저장용 draft_no 필드에만 쓴다.
export const computeResolutionDraftNo = (originalDraftNo, suffix) => `${originalDraftNo}-R${suffix}`;

// ✅ 목록/상세 등에서 결의서의 draft_no를 표시할 때 "-R{n}" 접미가 절대 노출되지
// 않도록 항상 template_data.internalDraftNo(원본 그대로)를 우선 쓴다 — 다른
// 문서종류는 이 필드가 없어 기존 draft_no 그대로 표시된다.
export const internalDraftNoOf = (doc) =>
  RESOLUTION_DOCUMENT_TYPES.includes(doc.document_type)
    ? doc.template_data?.internalDraftNo || doc.draft_no
    : doc.draft_no;

// ✅ 결의서의 거래일(지출결의=expenseDate/수입결의=depositDate) — 저장 시점에 각자
// 다른 키로 template_data에 쌓이므로 셋 다 확인한다(ResolutionPreview.jsx의
// buildResolutionProps와 동일 기준).
export const transactionDateOf = (doc) => {
  const td = doc.template_data || {};
  return td.expenseDate || td.depositDate || td.transactionDate || "";
};

// ✅ 기안자 소속(팀명) 매핑 — User 모델(accounts.models.User)에는 이름/직위/직급순서/
// 고용형태만 있고 소속·팀 필드가 전혀 없어(직위 기반 매핑도 불가 — "주임"이 5명인데
// 같은 팀이라는 보장이 없음) 이름 기준으로 매핑한다. 실무 확인 후 채운 값(대표이사
// 서복성은 기안 주체가 아니라 매핑 대상에서 제외).
export const DRAFTER_TEAM_BY_NAME = {
  "나주영": "경영지원팀",
  "김희민": "경영지원팀",
  "윤길상": "사업관리팀",
  "고재명": "사업관리팀",
  "강미희": "사업관리팀",
  "김재민": "사업관리팀",
  "장희영": "사업관리팀",
};

// ✅ 매핑에 없는 이름이면 빈 문자열 — drafterAffiliationOf가 이 경우 2번째 줄
// 자체를 생략하므로(">"가 꼬리로 남지 않음) 매핑 누락 시에도 화면이 깨지지 않는다.
export const drafterTeamOf = (name) => DRAFTER_TEAM_BY_NAME[name] || "";

// ✅ 기안자 표시 2번째 줄("금천일자리주식회사>본사직원>{팀명}") 조립 — 팀명이 없으면
// (매핑에 없는 이름, 예: 대표이사) ">"가 꼬리로 남지 않도록 줄 자체를 빈 문자열로
// 반환해 2번째 줄을 통째로 생략한다.
export const drafterAffiliationOf = (name) => {
  const team = drafterTeamOf(name);
  return team ? `금천일자리주식회사>본사직원>${team}` : "";
};
